// src/components/common/StatusUpdatePopup.tsx
import React, { useState } from 'react';
import Button from './Button';
import { ChevronDown } from 'lucide-react';

interface StatusUpdatePopupProps {
  isOpen: boolean;
  onClose: () => void;
  currentStatus: string;
  courseId: string;
  onUpdate: (courseId: string, newStatus: string) => Promise<void>;
}

const StatusUpdatePopup: React.FC<StatusUpdatePopupProps> = ({
  isOpen,
  onClose,
  currentStatus,
  courseId,
  onUpdate
}) => {
  const [selectedStatus, setSelectedStatus] = useState(currentStatus);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const statusOptions = [
    { value: 'Draft', label: 'Draft', color: 'text-blue-600 bg-blue-50' },
    { value: 'Published', label: 'Published', color: 'text-green-600 bg-green-50' },
    { value: 'Sold out', label: 'Sold out', color: 'text-blue-600 bg-blue-50' },
    // { value: 'Selling', label: 'Selling', color: 'text-green-600 bg-green-50' }
  ];
  
  const getStatusColor = (status: string) => {
    const option = statusOptions.find(opt => opt.value === status);
    return option ? option.color : 'text-gray-600 bg-gray-50';
  };
  
  const handleSubmit = async () => {
    if (selectedStatus === currentStatus) {
      onClose();
      return;
    }
    
    setIsSubmitting(true);
    try {
      await onUpdate(courseId, selectedStatus);
      onClose();
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4" style={{ zIndex: 9999 }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md relative">
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Update Status</h3>
          
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Status
            </label>
            <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium mb-4">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(currentStatus)}`}>
                {currentStatus}
              </span>
            </div>
            
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select New Status
            </label>
            <div className="relative">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm rounded-md appearance-none"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <ChevronDown className="h-4 w-4 text-gray-400" />
              </div>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3">
            <Button
              variant="secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Updating...' : 'Update Status'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatusUpdatePopup;